import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import {
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Button,
  Label,
  Input,
  FormFeedback,
} from 'reactstrap';

import { APIcreateMetadataGroup, APIupdateMetadataGroup } from '../api/Metadata';

import { fetchTaxonomies, fetchMetadataGroups, fetchCustomFields } from '../actions/Metadata';

import { addFlashMessage } from '../helpers/FlashMessages';
import { getCustomFieldTypeLabel } from '../helpers/Metadata';

import ErrorBoundary from './ErrorBoundary';
import ProcessingSpinner from './ProcessingSpinner';

const MetadataGroupModal = ({
  handleClose,
  taxonomies,
  customFields,
  metadataGroupId,
  metadataGroups,
  loadMetadataGroups,
  loadTaxonomies,
  loadCustomFields,
}) => {
  const intl = useIntl();

  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [assignedTaxonomies, setAssignedTaxonomies] = useState([]);
  const [assignedCustomFields, setAssignedCustomFields] = useState([]);
  const [errors, setErrors] = useState({});

  const handleSaveMetadataGroup = () => {
    setLoading(true);

    const newErrors = {};
    if (name) {
      newErrors.name = intl.formatMessage({ defaultMessage: 'Name is required' });
    }
    setErrors(newErrors);

    const metadataGroup = {
      ...(metadataGroupId && { id: metadataGroupId }),
      name,
      assigned_taxonomies: assignedTaxonomies,
      assigned_custom_fields: assignedCustomFields,
    };

    if (metadataGroupId) {
      APIupdateMetadataGroup(metadataGroup).then(() => {
        addFlashMessage({
          type: 'success',
          text: intl.formatMessage({ defaultMessage: 'The changes have been saved' }),
        });
        loadMetadataGroups();
        loadTaxonomies();
        loadCustomFields();
        handleClose();
      }).catch(() => {
        addFlashMessage({
          type: 'danger',
          text: intl.formatMessage({ defaultMessage: 'Something went wrong! Please try again.' }),
        });
      });
    } else {
      APIcreateMetadataGroup(metadataGroup).then(() => {
        addFlashMessage({
          type: 'success',
          text: intl.formatMessage({ defaultMessage: 'The new Metadata Group has been created' }),
        });
        loadMetadataGroups();
        loadTaxonomies();
        loadCustomFields();
        handleClose();
      }).catch(() => {
        addFlashMessage({
          type: 'danger',
          text: intl.formatMessage({ defaultMessage: 'Something went wrong! Please try again.' }),
        });
      });
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
      e.preventDefault();
      handleSaveMetadataGroup();
    }
  };

  const isTaxonomyAssigned = (t) => assignedTaxonomies.includes(t.id);

  const isCustomFieldAssigned = (t) => assignedCustomFields.includes(t.id);

  const toggleTaxonomyAssignment = (t) => {
    setAssignedTaxonomies(
      (prev) => (isTaxonomyAssigned(t) ? prev.filter((id) => id !== t.id) : [...prev, t.id]),
    );
  };

  const toggleCustomFieldsAssignment = (t) => {
    setAssignedCustomFields(
      (prev) => (isCustomFieldAssigned(t) ? prev.filter((id) => id !== t.id) : [...prev, t.id]),
    );
  };

  useEffect(() => {
    const group = metadataGroups.find(tg => tg.id === metadataGroupId);

    if (group) {
      setName(group.name);
      setAssignedTaxonomies(group.taxonomies);
      setAssignedCustomFields(group.custom_fields);
    }
  }, [metadataGroupId, metadataGroups]);

  const taxonomiesToAssign = taxonomies.filter(t => !t.ai_tagging);
  const customFieldsToAssign = customFields;

  return (
    <ErrorBoundary>
      <ModalHeader
        toggle={handleClose}
        className="modal-header-center"
      >
        {!metadataGroupId && intl.formatMessage({ defaultMessage: 'Add Metadata Group' })}
        {metadataGroupId && intl.formatMessage({ defaultMessage: 'Edit Metadata Group' })}
      </ModalHeader>
      <ModalBody>
        <Form
          className="metadata-group-modal-form"
          onKeyPress={handleKeyPress}
        >
          <FormGroup>
            <Label for="inpMetadataGroupName">
              {intl.formatMessage({ defaultMessage: 'Group name' })}
            </Label>
            <Input
              type="text"
              name="inpMetadataGroupName"
              id="inpMetadataGroupName"
              placeholder={intl.formatMessage({ defaultMessage: 'Group name' })}
              autoFocus={true}
              value={name}
              onChange={(e) => setName(e.target.value)}
              invalid={errors.inpMetadataGroupName !== undefined}
            />
            <FormFeedback>
              {errors.name}
            </FormFeedback>
          </FormGroup>
          <div className="metadata-group-modal-content-wrapper">
            <FormGroup>
              <Label>
                {intl.formatMessage({ defaultMessage: 'Assign Tag Categories' })}
              </Label>
              <div className="metadata-group-to-assign">
                {taxonomiesToAssign.map((t) => (
                  <Label
                    for={`metadata-group-taxonomy-to-assign-${t.id}`}
                    key={`metadata-group-taxonomy-to-assign-${t.id}`}
                    className="metadata-group-to-assign-element"
                  >
                    <div
                      className="metadata-group-to-assign-label"
                    >
                      <Input
                        className="input-check check-s form-check-input"
                        id={`metadata-group-taxonomy-to-assign-${t.id}`}
                        type="checkbox"
                        checked={isTaxonomyAssigned(t)}
                        onChange={() => toggleTaxonomyAssignment(t)}
                      />
                      <span>
                        {t.name}
                      </span>
                    </div>
                    <span>
                      {intl.formatMessage({ defaultMessage: '{count} tags' }, { count: t.terms.length })}
                    </span>
                  </Label>
                ))}
                {taxonomiesToAssign.length === 0 && (
                  <p className="metadata-group-to-assign-element">
                    {intl.formatMessage({ defaultMessage: 'No Tag Categories to assign' })}
                  </p>
                )}
              </div>
            </FormGroup>
            <FormGroup>
              <Label>
                {intl.formatMessage({ defaultMessage: 'Assign Custom Fields' })}
              </Label>
              <div className="metadata-group-to-assign">
                {customFieldsToAssign.map((cf) => (
                  <Label
                    for={`metadata-group-custom-field-to-assign-${cf.id}`}
                    key={`metadata-group-custom-field-to-assign-${cf.id}`}
                    className="metadata-group-to-assign-element"
                  >
                    <div
                      className="metadata-group-to-assign-label"
                    >
                      <Input
                        className="input-check check-s form-check-input"
                        id={`metadata-group-custom-field-to-assign-${cf.id}`}
                        type="checkbox"
                        checked={isCustomFieldAssigned(cf)}
                        onChange={() => toggleCustomFieldsAssignment(cf)}
                      />
                      <span>
                        {cf.name}
                      </span>
                    </div>
                    <span>
                      {getCustomFieldTypeLabel(cf)}
                    </span>
                  </Label>
                ))}
                {customFieldsToAssign.length === 0 && (
                  <p className="metadata-group-to-assign-element">
                    {intl.formatMessage({ defaultMessage: 'No Custom Fields to assign' })}
                  </p>
                )}
              </div>
            </FormGroup>
          </div>
        </Form>
      </ModalBody>
      <ModalFooter className="justify-r">
        <div className="buttons-group">
          <Button
            color="link"
            disabled={loading}
            onClick={handleClose}
          >
            {intl.formatMessage({ defaultMessage: 'Cancel' })}
          </Button>
          <Button
            color="primary"
            disabled={loading}
            onClick={handleSaveMetadataGroup}
          >
            <ProcessingSpinner show={loading} />
            {intl.formatMessage({ defaultMessage: 'Save' })}
          </Button>
        </div>
      </ModalFooter>
    </ErrorBoundary>
  );
};

const mapStateToProps = state => ({
  taxonomies: state.Metadata.taxonomies,
  customFields: state.Metadata.customFields,
  metadataGroups: state.Metadata.metadataGroups,
});

const mapDispatchToProps = dispatch => ({
  loadMetadataGroups: () => {
    dispatch(fetchMetadataGroups());
  },
  loadTaxonomies: () => {
    dispatch(fetchTaxonomies());
  },
  loadCustomFields: () => {
    dispatch(fetchCustomFields());
  },
});

MetadataGroupModal.propTypes = {
  taxonomies: PropTypes.array.isRequired,
  customFields: PropTypes.array.isRequired,
  metadataGroups: PropTypes.array.isRequired,
  metadataGroupId: PropTypes.number,
  handleClose: PropTypes.func.isRequired,
  loadMetadataGroups: PropTypes.func.isRequired,
  loadTaxonomies: PropTypes.func.isRequired,
  loadCustomFields: PropTypes.func.isRequired,
};

MetadataGroupModal.defaultProps = {
  metadataGroupId: null,
};

export default connect(mapStateToProps, mapDispatchToProps)(MetadataGroupModal);
